/* PDP image loupe — a round magnifier that follows the cursor over the gallery
   images, so you can inspect the design detail. DESKTOP / fine-pointer ONLY:
   it bails out on touch devices (no loupe on mobile). Pure vanilla, no deps. */
(() => {
  // touch / coarse pointer → no loupe at all.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const ZOOM = 3;     // magnification
  const SIZE = 190;   // lens diameter (px) — keep in sync with .product__loupe

  // ask the Shopify CDN for a crisp source for the lens.
  const hiRes = (src) => {
    try {
      const u = new URL(src, location.href);
      if (u.searchParams.has('width')) u.searchParams.set('width', '2048');
      return u.href;
    } catch (e) {
      return src;
    }
  };

  const init = (gallery) => {
    if (gallery.dataset.loupe) return;
    gallery.dataset.loupe = '1';
    if (getComputedStyle(gallery).position === 'static') {
      gallery.style.position = 'relative';
    }

    const lens = document.createElement('div');
    lens.className = 'product__loupe';
    lens.setAttribute('aria-hidden', 'true');
    gallery.appendChild(lens);

    // Warm the cache so the loupe shows INSTANTLY on first hover (the lag you
    // saw was the hi-res source being fetched on hover). Keep a reference to
    // each preloaded Image so its decoded bitmap stays around.
    //
    // SCOPED to the currently-visible size slide only: the PDP stacks one
    // .product__slide per size, but only the CHECKED size is on screen. Warming
    // every off-screen size's 2048px recto+verso up front fetched/decoded a
    // pile of large bitmaps the shopper may never see. We warm the visible
    // slide now and the others LAZILY when the size changes.
    const warmed = [];
    const warmImages = (imgs) => {
      imgs.forEach((img) => {
        const url = hiRes(img.currentSrc || img.src);
        if (warmed.some((p) => p.src === url)) return;
        const pre = new Image();
        pre.decoding = 'async';
        pre.src = url;
        warmed.push(pre);
      });
    };

    // the common product container holds BOTH the gallery slides and the size
    // radios (the radios live in the info column, not inside .product__gallery).
    const root = gallery.closest('product-media') || gallery.closest('.product') || document;

    // the slide that is actually on screen = the one whose data-size matches the
    // checked size radio (fallbacks: the server-rendered default, then the
    // first slide — covers JS-off-rendered single-size / no-radio products).
    const visibleSlide = () => {
      const checked = root.querySelector('[data-size-input]:checked');
      const size = checked && checked.dataset.size;
      if (size) {
        const match = gallery.querySelector('.product__slide[data-size="' + CSS.escape(size) + '"]');
        if (match) return match;
      }
      return gallery.querySelector('.product__slide--default') || gallery.querySelector('.product__slide') || gallery;
    };

    const warmVisible = () => {
      const slide = visibleSlide();
      if (slide) warmImages(Array.from(slide.querySelectorAll('.product__image')));
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(warmVisible, { timeout: 2500 });
    } else {
      setTimeout(warmVisible, 1000);
    }

    // lazily warm the newly-selected size's slide on size-change (so a hover
    // right after switching is still instant) — never the off-screen sizes.
    root.addEventListener('change', (e) => {
      if (e.target.matches && e.target.matches('[data-size-input]')) warmVisible();
    });

    let current = null;

    const hide = () => {
      lens.classList.remove('is-on');
      current = null;
    };

    const onMove = (e) => {
      const img = e.target.closest('.product__image');
      if (!img) return hide();

      const rect = img.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return hide();

      if (img !== current) {
        current = img;
        lens.style.backgroundImage = 'url("' + hiRes(img.currentSrc || img.src) + '")';
      }

      lens.style.backgroundSize = (rect.width * ZOOM) + 'px ' + (rect.height * ZOOM) + 'px';
      lens.style.backgroundPosition =
        (SIZE / 2 - x * ZOOM) + 'px ' + (SIZE / 2 - y * ZOOM) + 'px';

      const g = gallery.getBoundingClientRect();
      lens.style.left = (e.clientX - g.left - SIZE / 2) + 'px';
      lens.style.top = (e.clientY - g.top - SIZE / 2) + 'px';
      lens.classList.add('is-on');
    };

    gallery.addEventListener('mousemove', onMove);
    gallery.addEventListener('mouseleave', hide);
  };

  const start = () => document.querySelectorAll('.product__gallery').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  // re-init when the section reloads in the theme editor
  document.addEventListener('shopify:section:load', start);
})();
